import axios from "./AutoRefreshAxios";
import { AxiosRequestConfig } from "axios";
import jwtDecode from "jwt-decode";
import store from "../store";
import { getCookie } from "@/shared/scripts/helpers/cookie/cookie";
import { User } from "@/shared/interfaces/user/User.interface";
import {
    NOT_VALID,
    BAD_CREDENTIALS,
    SUCCESS,
    ERROR_SERVER_NOT_RESPONDING,
    AUTH_ERROR,
    NOT_AUTHORIZED,
    FORBIDDEN,
    EMAIL_NOT_VALIDATED,
    USER_DATA_ERROR_RESET_PASSWORD
} from "./helpers/constants/payload";
import { userHasRole } from "@/shared/scripts/helpers/function";
import { buildUserName } from "@/shared/scripts/helpers/user/user";

export function authenticate(): boolean {
    // check if auth cookie is present
    const rawCookie: string | void | Record<string, unknown> = getCookie("oauth_token_" + process.env.VUE_APP_ENVIRONMENT_NAME);

    if (!rawCookie) {
        // if cookie is not set, unlog
        store.commit("UserModule/setUser", null);

        anonymousLogin().then(status => {
            // eslint-disable-next-line
            console.log(status);
        });

        return false;
    }

    if (rawCookie) {
        // decode token
        const userData: Record<string, unknown> = jwtDecode(rawCookie as string);
        let rawAdminCookie: string|null = null;

        if(userData.modeMasquerade) {
            rawAdminCookie = getCookie("oauth_admin_token_" + process.env.VUE_APP_ENVIRONMENT_NAME) as string;
        }

        const authorizationsStatement = (userData.authorizations as Record<string,Array<Record<string,string>>>).statements;
        // fill user with cookie data
        const user: User = {
            id : userData.id as string,
            jwtToken : rawCookie as string,
            jwtAdmin: rawAdminCookie as string,
            username : buildUserName(userData.firstName, userData.lastName),
            isAnonymous: userHasRole("ANONYMOUS", authorizationsStatement),
            isMasquerading: userData.modeMasquerade as boolean,
            language : userData.language as string
        };

        const userEmail = userData.email;
        if (userEmail) {
            user.email = userEmail as string;
        }

        store.commit("UserModule/setUser", user);

        return true;
    } else {
        return false;
    }

}

export function login(username: string, password: string): Promise<string> {
    const baseUrl: string = process.env.VUE_APP_PHP_PORTAL_ENDPOINT || "";
    const uri = "login";
    const method = "post";

    const headers: Record<string,unknown> = {
        "Accept": "application/json",
        "x-client-source": "ENCODING_FUNNEL",
    };

    const config: AxiosRequestConfig & { skipAuthRefresh?: boolean } = {
        method: method,
        headers: headers,
        url: baseUrl + uri,
        data: {
            "login-email": username,
            "login-password": password,
            "login-remember": true,
            "site": "IMMOWEB_ALL"
        },
        withCredentials: true,
        skipAuthRefresh: true
    };

    return axios(config).then(res => {
        // authentication succeeded, save tokens in store and cookie
        if (res.data) {
            if (res.data.jwtToken) {
                // decode token
                const userData: Record<string, unknown> = jwtDecode(res.data.jwtToken);

                const user: User = {
                    "id" : userData.id as string,
                    "jwtToken" : res.data.jwtToken,
                    "username" : buildUserName(userData.firstName, userData.lastName),
                    "isAnonymous": false,
                    "isMasquerading": userData.modeMasquerade as boolean,
                    "language": userData.language as string
                };

                const userEmail = userData.email;
                if (userEmail) {
                    user.email = userEmail as string;
                }

                // saving user in store
                store.commit("UserModule/setUser", user);
            }
        }
        return SUCCESS;
    }).catch(e => {
        if (!e.response) {
            return ERROR_SERVER_NOT_RESPONDING;
        }

        // A portal 500 answers with an HTML page rather than JSON, so there may be no body to read.
        const data: Record<string, unknown> = e.response.data || {};

        if(e.response.status === NOT_VALID
            && data.error_description === BAD_CREDENTIALS) {
            return AUTH_ERROR;
        }
        // The account predates the current password rules, so the portal has already emailed a reset.
        if (e.response.status === NOT_AUTHORIZED
            && data.errorCode === USER_DATA_ERROR_RESET_PASSWORD) {
            return USER_DATA_ERROR_RESET_PASSWORD;
        }
        // The portal withholds the session until the address is confirmed, and says so with a bare
        // 403 carrying no error code.
        if (e.response.status === FORBIDDEN) {
            return EMAIL_NOT_VALIDATED;
        }
        // Every other 401 means "these credentials do not get you in": wrong password, too many
        // attempts, blocked account. The portal tells them apart (USER_AUTH_00001, USER_AUTH_00004_0,
        // USER_AUTH_00004_1) and we deliberately do not, the way the owner hub does it.
        if (e.response.status === NOT_AUTHORIZED) {
            return BAD_CREDENTIALS;
        }
        return ERROR_SERVER_NOT_RESPONDING;
    });
}

async function anonymousLogin() : Promise<string>
{
    const baseUrl: string = process.env.VUE_APP_PHP_PORTAL_ENDPOINT || "";
    const uri = "anonymous";
    const method = "post";

    try {
        const config: AxiosRequestConfig & { skipAuthRefresh?: boolean } = {
            method: method,
            url: baseUrl + uri,
            data: {
                "site": "IMMOWEB_ALL"
            },
            headers: {
                "Accept": "application/json",
                "x-client-source": "immoweb.funnel"
            },
            withCredentials: true,
            skipAuthRefresh: true
        };

        const res = await axios(config);
        // authentication succeeded, save tokens in store and cookie
        if (res.data) {
            if (res.data.jwtToken) {
                // decode token
                const userData: Record<string, unknown> = jwtDecode(res.data.jwtToken);
                const user: User = {
                    "id" : userData.id as string,
                    "jwtToken": res.data.jwtToken,
                    "username": buildUserName(userData.firstName, userData.lastName),
                    "isAnonymous": true,
                    "isMasquerading": userData.modeMasquerade as boolean,
                    "language": userData.language as string

                };

                store.commit("UserModule/setUser", user);

                return SUCCESS;
            }
        }
        return SUCCESS;
    }
    catch (e: any) {
        if (e.response.status === NOT_VALID
            && e.response.data.error_description === BAD_CREDENTIALS) {
            return AUTH_ERROR;
        }
        return ERROR_SERVER_NOT_RESPONDING;
    }
}

export function logout(): Promise<boolean>
{
    const baseUrl: string = process.env.VUE_APP_PHP_PORTAL_ENDPOINT || "";
    const uri = "logout";
    const method = "post";

    const headers: Record<string,unknown> = {
        "Accept": "application/json",
        "x-client-source": "ENCODING_FUNNEL",
    };

    const config: AxiosRequestConfig & { skipAuthRefresh?: boolean } = {
        method: method,
        headers: headers,
        url: baseUrl + uri,
        withCredentials: true,
        skipAuthRefresh: true
    };

    return axios(config).then(res => {
        if (res.data && res.data.success) {
            // delete user in store
            store.commit("UserModule/setUser", null);

            return true;
        }
        return true;
    }).catch(error => {
        throw new Error(error);
    });
}
